import React, { useEffect, useState } from "react";
import { Form, Button, Select, Checkbox, Flex, Popconfirm, Divider } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import GeneralModal from "../../General.modal";
import { Error, Success } from "../../../../commons/Messages/Messages";
import { generalErrorAlert } from "../../../../commons/GeneralErrorAlert";
import { DEFAULT_SHIFT_COLOR } from "../../../../utils/shiftUtils";
import { saveWithConflictCheck, shiftApi } from "../shiftApi";
import {
  ShiftFormFields,
  formValuesToShift,
  formValuesToTemplate,
  validateShiftValues
} from "./ShiftFormFields";

const defaultValues = { daysOfWeek: [], employees: [], minStaff: 0, color: DEFAULT_SHIFT_COLOR };

/**
 * "Add Shift" button + modal. Pass `open`/`onClose` to control it from outside
 * (e.g. clicking an empty calendar slot) and `initialValues` to prefill the form.
 */
const AddShifts = ({ updateParent, employees = [], open, onClose, initialValues, hideButton }) => {
  const { t } = useTranslation("dashboard");
  const [form] = Form.useForm();
  const controlled = open !== undefined;
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = controlled ? open : internalOpen;
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [saveAsTemplate, setSaveAsTemplate] = useState(false);

  const loadTemplates = async () => {
    try {
      const res = await shiftApi.get("/templates");
      setTemplates(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      generalErrorAlert(err);
    }
  };

  useEffect(() => {
    if (isOpen) {
      // The form store outlives the modal, so reset every field on each open
      form.setFieldsValue({
        title: undefined,
        range: undefined,
        ...defaultValues,
        ...initialValues
      });
      loadTemplates();
      setSaveAsTemplate(false);
    }
  }, [isOpen]);

  const close = () => (controlled ? onClose?.() : setInternalOpen(false));

  // Fill the form from a template, keeping any dates already picked
  const applyTemplate = id => {
    const template = templates.find(tpl => tpl._id === id);
    if (!template) return;
    const [from, to] = form.getFieldValue("range") || [];
    const firstDay = (from || dayjs()).startOf("day");
    const lastDay = (to || dayjs().add(4, "week")).startOf("day");
    const [sh, sm] = template.startTime.split(":").map(Number);
    const [eh, em] = template.endTime.split(":").map(Number);
    form.setFieldsValue({
      title: template.title,
      range: [firstDay.hour(sh).minute(sm), lastDay.hour(eh).minute(em)],
      daysOfWeek: (template.daysOfWeek || []).map(Number),
      employees: (template.employees || []).map(String),
      minStaff: template.minStaff || 0,
      color: template.color || DEFAULT_SHIFT_COLOR
    });
  };

  const deleteTemplate = async id => {
    try {
      const res = await shiftApi.send("delete", `/templates/${id}`);
      Success(res.data.successMessage);
      loadTemplates();
    } catch (err) {
      generalErrorAlert(err);
    }
  };

  const onSubmit = async () => {
    if (loading) return;
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    const problem = validateShiftValues(values, t);
    if (problem) {
      Error(problem);
      return;
    }
    setLoading(true);
    try {
      const res = await saveWithConflictCheck("post", "/add", formValuesToShift(values), t);
      if (!res) return;
      if (saveAsTemplate) await shiftApi.send("post", "/templates", formValuesToTemplate(values));
      Success(res.data.successMessage);
      updateParent && updateParent();
      close();
    } catch (err) {
      generalErrorAlert(err);
    } finally {
      setLoading(false);
    }
  };

  const items = (
    <Form form={form} layout="vertical" initialValues={{ ...defaultValues, ...initialValues }}>
      {templates.length > 0 && (
        <>
          <Form.Item label={t("Start from template")}>
            <Select
              allowClear
              placeholder={t("Choose a template")}
              onChange={applyTemplate}
              optionLabelProp="label"
            >
              {templates.map(tpl => (
                <Select.Option key={tpl._id} value={tpl._id} label={tpl.title}>
                  <Flex justify="space-between" align="center">
                    <span>
                      {tpl.title} · {tpl.startTime}–{tpl.endTime}
                    </span>
                    <Popconfirm
                      title={t("Delete this template?")}
                      onConfirm={e => {
                        e?.stopPropagation();
                        deleteTemplate(tpl._id);
                      }}
                      onCancel={e => e?.stopPropagation()}
                    >
                      <DeleteOutlined onClick={e => e.stopPropagation()} />
                    </Popconfirm>
                  </Flex>
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Divider style={{ margin: "8px 0 16px" }} />
        </>
      )}
      <ShiftFormFields employees={employees} t={t} />
      <Checkbox checked={saveAsTemplate} onChange={e => setSaveAsTemplate(e.target.checked)}>
        {t("Also save as template")}
      </Checkbox>
    </Form>
  );

  return (
    <div className="AddShifts">
      {!hideButton && (
        <Button type="primary" onClick={() => setInternalOpen(true)}>
          {t("Add Shift")}
        </Button>
      )}
      <GeneralModal
        title={t("Add Shift")}
        open={isOpen}
        onOk={onSubmit}
        okText={t("Save")}
        onCancel={close}
        items={items}
      />
    </div>
  );
};

export default AddShifts;
